import { useState } from 'react';

import type {
  AnalysisOutput,
  ProviderId,
  UploadPreview,
} from '@smartapply/shared';

import { PageShell } from '../components/layout/PageShell.js';
import { UploadZone } from '../components/upload/UploadZone.js';
import { ProviderForm } from '../components/provider/ProviderForm.js';
import { DiagnosisView } from '../components/diagnosis/DiagnosisView.js';
import { AnalyzingState } from '../components/diagnosis/AnalyzingState.js';
import { Stamp } from '../components/ui/Stamp.js';
import { SmartApplyApiError, runAnalyze } from '../lib/api-client.js';
import {
  clearProfileSession,
  loadProfileSession,
  savePreview,
  saveAnalysis,
} from '../lib/profile-session.js';

type Step =
  | { kind: 'upload' }
  | { kind: 'configure'; preview: UploadPreview }
  | { kind: 'analyzing'; preview: UploadPreview; modelId: string }
  | { kind: 'done'; output: AnalysisOutput }
  | { kind: 'error'; message: string };

/**
 * Reidrata o passo a partir do sessionStorage. Se já tem análise pronta,
 * pula direto pra tela de diagnóstico — evita reupload + reanalise toda
 * vez que o user navega pra outra rota e volta.
 */
function initialStep(): Step {
  const session = loadProfileSession();
  if (session.output) {
    return { kind: 'done', output: session.output };
  }
  if (session.preview) {
    return { kind: 'configure', preview: session.preview };
  }
  return { kind: 'upload' };
}

export function HomeRoute() {
  const [step, setStep] = useState<Step>(initialStep);

  function handleUploaded(preview: UploadPreview) {
    savePreview(preview);
    setStep({ kind: 'configure', preview });
  }

  async function handleProviderConfigured(_provider: ProviderId, modelId: string) {
    if (step.kind !== 'configure') return;
    const preview = step.preview;

    setStep({ kind: 'analyzing', preview, modelId });

    try {
      const output = await runAnalyze({
        snapshotId: preview.snapshotId,
        options: {
          depth: 'standard',
          includeRewrites: true,
          rewriteSections: ['headline', 'summary'],
        },
      });
      saveAnalysis(output, modelId);
      setStep({ kind: 'done', output });
    } catch (err) {
      const message =
        err instanceof SmartApplyApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Falha desconhecida durante a análise.';
      // Se o snapshot expirou no server (restart do container ou TTL > 1h),
      // o melhor é forçar reupload — guardar análise antiga seria mentir.
      if (
        err instanceof SmartApplyApiError &&
        (err.code === 'snapshot_missing' || err.code === 'session_missing')
      ) {
        clearProfileSession();
      }
      setStep({ kind: 'error', message });
    }
  }

  function reset() {
    clearProfileSession();
    setStep({ kind: 'upload' });
  }

  return (
    <PageShell>
      {step.kind === 'upload' && <UploadStep onUploaded={handleUploaded} />}

      {step.kind === 'configure' && (
        <ConfigureStep
          preview={step.preview}
          onConfigured={(p, m) => void handleProviderConfigured(p, m)}
        />
      )}

      {step.kind === 'analyzing' && <AnalyzingState modelId={step.modelId} />}

      {step.kind === 'done' && <DiagnosisView output={step.output} onReset={reset} />}

      {step.kind === 'error' && <ErrorState message={step.message} onReset={reset} />}
    </PageShell>
  );
}

function UploadStep({ onUploaded }: { onUploaded: (p: UploadPreview) => void }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_320px] lg:gap-16">
      <div>
        <Hero />
        <div className="mt-10 sm:mt-12">
          <UploadZone onUploaded={onUploaded} />
        </div>
      </div>
      <aside className="border-t border-[var(--border-subtle)] pt-8 lg:border-t-0 lg:pt-0">
        <SideNote />
      </aside>
    </div>
  );
}

function ConfigureStep({
  preview,
  onConfigured,
}: {
  preview: UploadPreview;
  onConfigured: (p: ProviderId, m: string) => void;
}) {
  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_320px] lg:gap-16">
      <div>
        <PreviewBanner preview={preview} />
        <div className="mt-10 sm:mt-12">
          <ProviderForm onConfigured={onConfigured} />
        </div>
      </div>
      <aside className="border-t border-[var(--border-subtle)] pt-8 lg:border-t-0 lg:pt-0">
        <ConfigureSideNote />
      </aside>
    </div>
  );
}

function Hero() {
  return (
    <section>
      <p className="mb-4 inline-block">
        <Stamp>Etapa 01 / 03 · upload</Stamp>
      </p>
      <h1 className="font-serif text-[32px] font-medium leading-[1.05] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[44px] lg:text-[52px]">
        Um diagnóstico
        <br />
        do seu perfil
        <span className="text-[var(--accent-saffron)]">.</span>
      </h1>
      <p className="mt-5 max-w-xl font-sans text-[15px] leading-relaxed text-[var(--ink-secondary)] sm:mt-6 sm:text-base">
        Você sobe o PDF do LinkedIn, cola sua API key de Anthropic, OpenAI, Google ou OpenRouter,
        e recebe um diagnóstico com forças, problemas críticos, quick wins de 30 minutos e textos
        prontos pra colar no perfil.
      </p>
    </section>
  );
}

function SideNote() {
  return (
    <div className="sticky top-12 space-y-8 border-l border-[var(--border-subtle)] pl-6">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          como funciona
        </p>
        <ol className="mt-3 space-y-3 font-sans text-sm text-[var(--ink-secondary)]">
          <li className="flex gap-3">
            <span className="font-mono text-xs tabular-nums text-[var(--ink-muted)]">01</span>
            <span>
              Exporte o PDF do seu LinkedIn (<em>Recursos &rsaquo; Salvar em PDF</em>).
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-xs tabular-nums text-[var(--ink-muted)]">02</span>
            <span>Configure provider de IA + cole sua API key.</span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-xs tabular-nums text-[var(--ink-muted)]">03</span>
            <span>Aguarde 15-40 segundos. Receba o diagnóstico.</span>
          </li>
        </ol>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          o que você recebe
        </p>
        <ul className="mt-3 space-y-2 font-sans text-sm text-[var(--ink-secondary)]">
          <li>→ 1-8 forças a preservar em reescrita</li>
          <li>→ 5-12 findings (críticos + sugestões)</li>
          <li>→ Até 8 quick wins (≤30 min cada)</li>
          <li>→ Reescritas prontas (headline, resumo)</li>
          <li>→ Posicionamento em uma frase</li>
        </ul>
      </div>
      <p className="border-t border-[var(--border-subtle)] pt-4 font-sans text-xs italic leading-relaxed text-[var(--ink-tertiary)]">
        “Caderno de revisor sobre folha de rascunho” — não é dashboard, é{' '}
        <span className="not-italic">artefato editorial</span> sobre o seu perfil.
      </p>
    </div>
  );
}

function ConfigureSideNote() {
  return (
    <div className="sticky top-12 space-y-8 border-l border-[var(--border-subtle)] pl-6">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          BYOK · sua chave, sua conta
        </p>
        <p className="mt-3 font-sans text-sm leading-relaxed text-[var(--ink-secondary)]">
          A análise roda na sua conta do provider escolhido. SmartApply não cobra pela operação
          de IA — você paga o provider direto, no preço dele.
        </p>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          custo médio por análise
        </p>
        <dl className="mt-3 space-y-2 font-sans text-xs">
          <div className="flex items-baseline gap-2">
            <dt className="text-[var(--ink-tertiary)]">Haiku 4.5 / Flash</dt>
            <span className="flex-1 translate-y-[-2px] border-b border-dotted border-[var(--border-default)]" />
            <dd className="font-mono tabular-nums text-[var(--ink-primary)]">~$0.02</dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt className="text-[var(--ink-tertiary)]">Sonnet 4.5 / GPT-4o</dt>
            <span className="flex-1 translate-y-[-2px] border-b border-dotted border-[var(--border-default)]" />
            <dd className="font-mono tabular-nums text-[var(--ink-primary)]">~$0.15</dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt className="text-[var(--ink-tertiary)]">Opus 4.1</dt>
            <span className="flex-1 translate-y-[-2px] border-b border-dotted border-[var(--border-default)]" />
            <dd className="font-mono tabular-nums text-[var(--ink-primary)]">~$0.40</dd>
          </div>
        </dl>
      </div>
      <p className="border-t border-[var(--border-subtle)] pt-4 font-sans text-xs italic leading-relaxed text-[var(--ink-tertiary)]">
        Sua key fica em memória de sessão, descartada quando você fecha a aba. Nunca em banco,
        nunca em log.
      </p>
    </div>
  );
}

function ExtraStat({
  label,
  value,
  hideIfZero,
}: {
  label: string;
  value: number | string;
  hideIfZero?: boolean;
}) {
  if (hideIfZero && (value === 0 || value === '0' || value === '0 chars')) return null;
  return (
    <div>
      <dt className="text-[var(--ink-tertiary)]">{label}</dt>
      <dd className="font-mono tabular-nums text-[var(--ink-primary)]">{value}</dd>
    </div>
  );
}

function PreviewBanner({ preview }: { preview: UploadPreview }) {
  const p = preview.preview;
  return (
    <section>
      <p className="mb-4 inline-block">
        <Stamp variant="positive">Etapa 02 / 03 · provider</Stamp>
      </p>
      <h2 className="font-serif text-3xl font-medium leading-tight text-[var(--ink-primary)]">
        PDF recebido.
      </h2>
      <div className="mt-6 border-l-2 border-[var(--accent-forest)] py-2 pl-5">
        <p className="font-serif text-lg text-[var(--ink-primary)]">{p.fullName}</p>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 font-sans text-xs text-[var(--ink-secondary)] sm:grid-cols-4">
          <ExtraStat label="headline" value={`${p.headlineLength} chars`} />
          <ExtraStat label="resumo" value={`${p.summaryLength} chars`} />
          <ExtraStat label="experiências" value={p.sectionsDetected.experiencesCount} />
          <ExtraStat label="skills (top 3)" value={p.sectionsDetected.skillsTop3Count} />
          <ExtraStat label="skills (total)" value={p.sectionsDetected.allSkillsCount} hideIfZero />
          <ExtraStat label="certificações" value={p.sectionsDetected.certificationsCount} />
          <ExtraStat label="educação" value={p.sectionsDetected.educationsCount} />
          <ExtraStat label="idiomas" value={p.sectionsDetected.languagesCount} hideIfZero />
          <ExtraStat label="em destaque" value={p.sectionsDetected.featuredCount} hideIfZero />
          <ExtraStat label="serviços" value={p.sectionsDetected.servicesCount} hideIfZero />
          <ExtraStat label="recomendações" value={p.sectionsDetected.recommendationsCount} hideIfZero />
          <ExtraStat label="voluntariado" value={p.sectionsDetected.volunteerCount} hideIfZero />
          <ExtraStat label="publicações" value={p.sectionsDetected.publicationsCount} hideIfZero />
          <ExtraStat label="projetos" value={p.sectionsDetected.projectsCount} hideIfZero />
          <ExtraStat label="prêmios" value={p.sectionsDetected.honorsAwardsCount} hideIfZero />
          <ExtraStat label="cursos" value={p.sectionsDetected.coursesCount} hideIfZero />
          <ExtraStat label="resultados" value={p.sectionsDetected.testScoresCount} hideIfZero />
          <ExtraStat label="organizações" value={p.sectionsDetected.organizationsCount} hideIfZero />
          <ExtraStat label="seções custom" value={p.sectionsDetected.customSectionsCount} hideIfZero />
        </dl>
      </div>
      {p.warnings.length > 0 && (
        <ul className="mt-4 space-y-1">
          {p.warnings.map((w, i) => (
            <li key={i} className="font-sans text-xs text-[var(--accent-saffron)]">
              ⚠ {w.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ErrorState({ message, onReset }: { message: string; onReset: () => void }) {
  return (
    <section className="mx-auto max-w-2xl py-12">
      <p className="mb-4 inline-block">
        <Stamp variant="critical">Erro</Stamp>
      </p>
      <h2 className="font-serif text-3xl font-medium text-[var(--accent-crimson)]">
        A análise falhou.
      </h2>
      <p className="mt-4 font-sans text-base text-[var(--ink-primary)]">{message}</p>
      <p className="mt-3 font-sans text-sm text-[var(--ink-tertiary)]">
        Possíveis causas: API key sem créditos, modelo escolhido fora do ar, timeout. Tente outro
        modelo ou recomece.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-8 rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90"
      >
        Recomeçar
      </button>
    </section>
  );
}
